import React from 'react'
import { Link, useForm, usePage } from '@inertiajs/inertia-react'
import Breadcrumbs from '../../../Shared/Components/Breadcrumbs'
import FormValidationSummary, {
    mergeValidationErrors,
    validationMessage,
} from '../../../Shared/Components/FormValidationSummary'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import SectionBadge from '../SectionBadge'
import '../announcements.css'

const PRESET_COLORS = [
    '#1e88e5', '#43a047', '#b89c1c', '#8e24aa', '#e53935', '#fb8c00',
    '#00897b', '#3949ab', '#d81b60', '#6d4c41', '#546e7a', '#1e2a4a',
]

const HEX_PATTERN = /^#[0-9a-f]{6}$/i

const SectionForm = ({ section, nextSortOrder }) => {
    const { errors: pageErrors } = usePage().props
    const isEdit = Boolean(section)

    const form = useForm({
        name: section?.name || '',
        color: section?.color || PRESET_COLORS[0],
        sort_order: section?.sort_order ?? nextSortOrder,
        is_active: section ? Boolean(section.is_active) : true,
    })

    const allErrors = mergeValidationErrors(form.errors, pageErrors)
    const fieldError = (key) => validationMessage(allErrors, key)
    const validColor = HEX_PATTERN.test(form.data.color)

    const onHexChange = (value) => {
        const next = value.startsWith('#') ? value : `#${value}`
        form.setData('color', next.slice(0, 7))
    }

    const submit = (e) => {
        e.preventDefault()
        const options = { preserveScroll: true }
        if (isEdit) {
            form.put(route('announcements.sections.update', section.id), options)
        } else {
            form.post(route('announcements.sections.store'), options)
        }
    }

    return (
        <Row>
            <FormValidationSummary errors={allErrors} />
            <span className="pb-7 pt-7">
                <Breadcrumbs
                    currentPath={
                        isEdit ? `admin/comunicados/secciones/${section.id}/editar` : 'admin/comunicados/secciones/nueva'
                    }
                />
            </span>

            <Card className="card-flush shadow-sm mb-10">
                <Card.Header className="pt-7">
                    <Card.Title className="fw-bold text-gray-800">{isEdit ? 'Editar sección' : 'Nueva sección'}</Card.Title>
                </Card.Header>
                <Card.Body className="py-8 px-6 px-lg-10">
                    <Form onSubmit={submit} style={{ maxWidth: 720 }}>
                        <Row className="g-6 mb-6">
                            <Col md={8}>
                                <Form.Label className="fw-bold required">Nombre</Form.Label>
                                <Form.Control
                                    value={form.data.name}
                                    onChange={(e) => form.setData('name', e.target.value)}
                                    maxLength={100}
                                    isInvalid={Boolean(fieldError('name'))}
                                />
                                <Form.Control.Feedback type="invalid">{fieldError('name')}</Form.Control.Feedback>
                            </Col>
                            <Col md={4}>
                                <Form.Label className="fw-bold required">Orden</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0}
                                    value={form.data.sort_order}
                                    onChange={(e) => form.setData('sort_order', e.target.value)}
                                    isInvalid={Boolean(fieldError('sort_order'))}
                                />
                                <Form.Control.Feedback type="invalid">{fieldError('sort_order')}</Form.Control.Feedback>
                            </Col>
                        </Row>

                        <div className="mb-6">
                            <Form.Label className="fw-bold required">Color</Form.Label>
                            <div className="d-flex flex-wrap align-items-center gap-4">
                                <label
                                    className="announcements-color-swatch"
                                    style={{ backgroundColor: validColor ? form.data.color : '#ffffff' }}
                                    title="Elegir cualquier color"
                                >
                                    <input
                                        type="color"
                                        value={validColor ? form.data.color : '#000000'}
                                        onChange={(e) => form.setData('color', e.target.value)}
                                    />
                                </label>
                                <Form.Control
                                    value={form.data.color}
                                    onChange={(e) => onHexChange(e.target.value.trim())}
                                    isInvalid={Boolean(fieldError('color')) || !validColor}
                                    style={{ width: 120 }}
                                    className="font-monospace"
                                    placeholder="#1e88e5"
                                />
                                <div className="d-flex flex-wrap gap-2">
                                    {PRESET_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            className={`announcements-color-preset ${
                                                form.data.color.toLowerCase() === color ? 'is-active' : ''
                                            }`}
                                            style={{ backgroundColor: color }}
                                            title={color}
                                            onClick={() => form.setData('color', color)}
                                        />
                                    ))}
                                </div>
                            </div>
                            {(fieldError('color') || !validColor) && (
                                <div className="invalid-feedback d-block">
                                    {fieldError('color') || 'Usa un color hexadecimal, por ejemplo #1e88e5.'}
                                </div>
                            )}
                            <div className="mt-4 d-flex align-items-center gap-3">
                                <span className="text-muted fs-7">Vista previa:</span>
                                <SectionBadge
                                    section={{ name: form.data.name || 'Sección', color: validColor ? form.data.color : '#e1e3ea' }}
                                    className="fs-7"
                                />
                            </div>
                        </div>

                        <Form.Check
                            type="switch"
                            id="section-active"
                            label="Activa (disponible al publicar comunicados)"
                            className="mb-8"
                            checked={form.data.is_active}
                            onChange={(e) => form.setData('is_active', e.target.checked)}
                        />

                        <div className="d-flex gap-3">
                            <Button type="submit" variant="primary" disabled={form.processing || !validColor}>
                                {form.processing ? 'Guardando…' : 'Guardar'}
                            </Button>
                            <Link href={route('announcements.sections.index')} className="btn btn-light">
                                Cancelar
                            </Link>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Row>
    )
}

SectionForm.layout = (page) => (
    <Layout title={page.props.section ? 'Editar sección' : 'Nueva sección'}>{page}</Layout>
)

export default SectionForm
